import {
  buildComputedLineItem,
  formatCommissionPreviewGstRate,
  formatInvoiceMoney,
  formatLineItemTaxRateDisplay,
  getLineDisplayTotalAmount,
  resolveExTaxFooterTotals,
  resolvePreviewTaxLabel,
} from "./invoiceLineItemHelpers";
import {
  resolveInvoiceDisplayTitle,
  getBranchDisplayName,
  getBranchAddress,
  getBranchCountry,
  formatBranchContactNumber,
  getBranchEmail,
  parsePaymentAdviceContent,
  parseTermsConditionsContent,
  buildBranchFromExtraLines,
  parseBrandingCompanyDetails,
  resolveInvoiceDocumentAmountDue,
  resolveInvoicePreviewTotals,
  buildClassicInvoiceBillToLines,
  buildClassicInvoiceCompanyContact,
  buildInvoiceLessPaidLabelParts,
  formatInvoicePreviewMetaDate,
  prepareFromPartyFields,
  filterPaymentAdviceBlocks,
} from "./invoicePreviewHelpers";
import {
  buildCommissionInvoiceBillToLines,
  buildCommissionInvoicePartyFields,
} from "../components/reports/commissions/commissionBillToUtils";
import {
  normalizeInvoiceDisplayOptions,
  toCommissionPartyPrintOptions,
} from "../components/reports/commissions/commissionInvoiceDisplayOptions";
import { buildInvoiceClientPartyFields } from "./applicationCostHelpers";
import {
  resolveInvoiceEmailBrandLogoUrl,
  getInvoiceSettingTradingName,
  getInvoiceSettingAddressLines,
  getInvoiceSettingPhoneLines,
  getInvoiceSettingEmail,
  formatInvoiceSettingWebsite,
} from "./invoiceSettingHelpers";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatPreviewDate(value) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return escapeHtml(value);
  return escapeHtml(
    date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }),
  );
}

function formatAmount(value) {
  return escapeHtml(formatInvoiceMoney(value, "", {}));
}

function renderLessPaidTotalRow(paymentDate, value, options = {}) {
  const { showDividerBefore = false } = options;
  const { prefix, detail } = buildInvoiceLessPaidLabelParts(paymentDate);
  const borderTop = showDividerBefore
    ? "border-top:1px solid #d1d5db;padding-top:10px;margin-top:8px;"
    : "";

  return `
    <tr>
      <td style="padding:2px 0;text-align:left;line-height:1.35;font-size:13px;${borderTop}">
        <span style="font-weight:700;color:#111827;">${escapeHtml(prefix)}</span>
        <span style="margin-left:4px;font-style:italic;font-weight:500;font-size:12px;color:#475569;">${escapeHtml(detail)}</span>
      </td>
      <td style="padding:2px 0;text-align:right;white-space:nowrap;font-weight:700;font-size:13px;line-height:1.35;color:#111827;${borderTop}">
        ${value}
      </td>
    </tr>
  `;
}

function renderTotalRow(label, value, options = {}) {
  const { variant = "", showDividerBefore = false, forPdf = false } = options;
  const borderTop =
    !forPdf && showDividerBefore
      ? "border-top:1px solid #111827;padding-top:8px;margin-top:6px;"
      : "";
  const cellPad = forPdf ? "padding:4px 0;" : "padding:2px 0;";
  let labelExtra = "color:#111827;font-weight:400;";
  let valueExtra = "color:#111827;font-weight:400;";
  if (variant === "discount") {
    labelExtra = "color:#111827;font-weight:600;";
    valueExtra = "color:#111827;font-weight:600;";
  }
  if (variant === "grand" || variant === "due") {
    labelExtra = "font-size:13px;font-weight:700;color:#111827;";
    valueExtra = "font-size:13px;font-weight:700;color:#111827;";
  }
  if (variant === "tax" || variant === "paid") {
    labelExtra = "color:#111827;font-weight:600;";
    valueExtra = "color:#111827;font-weight:600;";
  }

  const isBold = variant === "grand" || variant === "due";
  const labelHtml = isBold
    ? `<strong style="font-weight:700;">${escapeHtml(label)}</strong>`
    : escapeHtml(label);
  const valueHtml = isBold
    ? `<strong style="font-weight:700;">${value}</strong>`
    : value;

  return `
    <tr>
      <td style="${cellPad}text-align:left;font-size:13px;line-height:1.35;${borderTop}${labelExtra}">
        ${labelHtml}
      </td>
      <td style="${cellPad}text-align:right;white-space:nowrap;font-size:13px;line-height:1.35;${borderTop}${valueExtra}">
        ${valueHtml}
      </td>
    </tr>
  `;
}

const EMAIL_INVOICE_PDF_OVERRIDES_CSS = `
  body, .invoice-pdf-body { margin: 0; padding: 0; background: #ffffff; }
  .sheet { border: none; max-width: none; margin: 0; width: 100%; }
  .sheet-body { margin: 0 !important; padding: 0 !important; }
  .sheet { margin: 0 !important; padding: 0 !important; }
  .parties { margin: 0 !important; }
  .party-label { margin: 0 0 4px 0 !important; font-weight: 700 !important; color: #4b5563 !important; }
  .party-name { font-weight: 700 !important; margin: 0 0 4px 0 !important; }
  .party-field, .party-line { margin: 0 0 2px 0 !important; }
  .party-field-label { font-weight: 700 !important; color: #111827 !important; }
  .meta-label { font-weight: 600 !important; color: #4b5563 !important; }
  .meta-value { font-weight: 700 !important; }
  .lines { margin: 0 !important; }
  .totals-wrap { margin: 0 !important; }
  .totals-align { margin: 0 0 12px 0 !important; }
  .totals { margin: 0 !important; }
  .footer { margin: 0 !important; padding: 4px 0 0 0 !important; border-top: 1px solid #e5e7eb; }
  .footer-heading { margin: 0 0 6px 0 !important; font-size: 14px !important; }
  .footer-line, .footer-field { font-size: 13px !important; line-height: 1.5 !important; }
  .footer-field-label, .footer-field-value { font-size: 13px !important; }
`;

/** Styles mirrored from invoice-xero-preview modal sheet (clients.css). */
const EMAIL_INVOICE_CSS = `
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 24px 16px;
    background: #e8edf2;
    color: #111827;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: 13px;
    line-height: 1.5;
    -webkit-text-size-adjust: 100%;
  }
  .sheet {
    width: 100%;
    max-width: 210mm;
    margin: 0 auto;
    background: #ffffff;
    border: 1px solid #dce3ea;
    padding: 0;
    color: #111827;
    overflow: hidden;
  }
  .header-band {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 14px 40px 12px;
    background: #ebf7ed;
    border-bottom: 1px solid #dce5ef;
    position: relative;
  }
  .header-band::after {
    content: "";
    position: absolute;
    left: 40px;
    right: 40px;
    bottom: 0;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, #0c2268 0%, #1a4f9c 52%, #2a9d6e 100%);
    opacity: 0.9;
  }
  .header-brand img {
    display: block;
    max-width: 170px;
    max-height: 52px;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: left center;
  }
  .header-title-wrap {
    flex: 1;
    min-width: 0;
    text-align: right;
  }
  .doc-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    line-height: 1.1;
    color: #0c2268;
  }
  .header-meta {
    margin-top: 2px;
    font-size: 12px;
    line-height: 1.35;
    color: #64748b;
  }
  .header-meta-value {
    margin-left: 6px;
    font-weight: 600;
    color: #334155;
  }
  .sheet-body {
    padding: 28px 40px 32px;
  }
  .masthead {
    margin-bottom: 36px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e5e7eb;
  }
  .brand {
    margin-bottom: 16px;
  }
  .brand img {
    display: block;
    max-width: 260px;
    max-height: 96px;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: left center;
  }
  .parties {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 40px;
    table-layout: fixed;
  }
  .parties > tbody > tr > td {
    vertical-align: top;
    width: 33.33%;
    padding: 0 18px 0 0;
  }
  .parties > tbody > tr > td:last-child {
    padding-right: 0;
    text-align: right;
  }
  .party-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #6b7280;
    margin: 0 0 8px;
  }
  .party-name {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 6px;
    line-height: 1.45;
  }
  .party-line,
  .party-field {
    color: #374151;
    font-size: 13px;
    margin: 0 0 4px;
    line-height: 1.55;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  .party-field-label {
    font-weight: 600;
    color: #111827;
  }
  .party-field-value {
    font-weight: 400;
    color: #374151;
  }
  .meta-row {
    margin: 0 0 4px;
    font-size: 13px;
    line-height: 1.55;
  }
  .meta-label {
    color: #6b7280;
    font-weight: 400;
    padding-right: 16px;
  }
  .meta-value {
    color: #111827;
    font-weight: 600;
    text-align: right;
    min-width: 108px;
  }
  .lines {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 24px;
  }
  .lines th {
    text-align: left;
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    background: #ebf7ed;
    border-bottom: 1px solid #d1d5db;
    padding: 10px 8px;
    white-space: nowrap;
  }
  .lines th.text-end,
  .lines td.text-end { text-align: right; }
  .lines td {
    border-bottom: 1px solid #f0f0f0;
    padding: 10px 8px;
    vertical-align: top;
    color: #111827;
    font-size: 13px;
  }
  .line-desc-text {
    line-height: 1.5;
    word-break: break-word;
    white-space: pre-line;
    color: #111827;
  }
  .line-desc-meta {
    margin-top: 4px;
    color: #6b7280;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.03em;
  }
  .totals-wrap {
    width: 100%;
  }
  .totals-wrap table {
    margin-left: auto;
  }
  .totals {
    width: 320px;
    max-width: 100%;
    border-collapse: collapse;
  }
  .footer {
    margin-top: 28px;
    padding-top: 18px;
    border-top: 1px solid #e5e7eb;
  }
  .footer-grid {
    width: 100%;
    border-collapse: collapse;
  }
  .footer-grid td {
    vertical-align: top;
    width: 50%;
    padding: 0 28px 0 0;
  }
  .footer-grid td:last-child {
    padding-right: 0;
  }
  .footer-heading {
    font-size: 13px;
    font-weight: 700;
    margin: 0 0 8px;
    color: #111827;
  }
  .footer-line {
    color: #374151;
    font-size: 13px;
    line-height: 1.55;
    margin: 0 0 4px;
  }
  .footer-field {
    display: table;
    width: 100%;
    margin: 0 0 2px;
    font-size: 13px;
    line-height: 1.4;
    border-collapse: collapse;
  }
  .footer-field-label,
  .footer-field-value {
    display: table-cell;
    vertical-align: top;
    padding: 0;
  }
  .footer-field-label {
    width: 1%;
    white-space: nowrap;
    font-weight: 600;
    color: #111827;
    padding-right: 8px;
  }
  .footer-field-value {
    color: #374151;
    word-break: break-word;
    overflow-wrap: anywhere;
    width: 99%;
  }
  .footer-field--stacked {
    display: block;
    margin-bottom: 4px;
  }
  .footer-field--stacked .footer-field-label,
  .footer-field--stacked .footer-field-value {
    display: block;
    width: 100%;
    padding-right: 0;
  }
  .footer-field--stacked .footer-field-value {
    margin-top: 1px;
  }
  .sheet--classic {
    border: none;
  }
  .classic-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    padding: 24px 40px 0;
  }
  .classic-title {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #111827;
    line-height: 1.1;
  }
  .classic-logo {
    display: block;
    max-width: 240px;
    max-height: 72px;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: right top;
    margin-left: auto;
  }
  .classic-info {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 28px;
  }
  .classic-info td {
    vertical-align: top;
    padding: 18px 24px 0 0;
    font-size: 13px;
    line-height: 1.55;
    color: #111827;
  }
  .classic-info td:first-child {
    width: 36%;
  }
  .classic-info td:nth-child(2) {
    width: 24%;
    min-width: 200px;
  }
  .classic-info td:last-child {
    width: 40%;
    padding-right: 0;
  }
  .classic-billto-label {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 4px;
  }
  .classic-billto-line {
    margin: 0 0 2px;
  }
  .classic-meta-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    margin: 0 0 6px;
    font-size: 13px;
    line-height: 1.45;
    align-items: start;
  }
  .classic-meta-label {
    font-weight: 700;
    color: #111827;
  }
  .classic-meta-value {
    font-weight: 400;
    color: #111827;
    white-space: nowrap;
    text-align: right;
  }
  .classic-company .classic-meta-row {
    grid-template-columns: 88px minmax(0, 1fr);
  }
  .classic-company .classic-meta-value {
    text-align: left;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  .classic-company-name {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.5;
    color: #111827;
    margin-bottom: 6px;
  }
  .sheet--classic .lines th {
    background: transparent;
    border-bottom: 1px solid #111827;
  }
  .sheet-body--classic {
    padding: 0 40px 32px;
  }
  @media only screen and (max-width: 640px) {
    body { padding: 12px 8px; }
    .sheet { padding: 28px 20px 24px; }
    .parties > tbody > tr > td,
    .footer-grid td {
      display: block;
      width: 100% !important;
      padding: 0 0 20px !important;
      text-align: left !important;
    }
    .meta-row { text-align: left !important; }
    .totals { width: 100%; }
  }
`;

function buildFromBlock(draft) {
  const { branding, branch, invoiceSetting } = draft;

  const companyName =
    getInvoiceSettingTradingName(invoiceSetting) || "Aptitude Migration";
  const branchName = getBranchDisplayName(branch);
  const branchAddress = getBranchAddress(branch);
  const branchCountry = getBranchCountry(branch);
  const branchPhone = formatBranchContactNumber(branch);
  const branchEmail = getBranchEmail(branch);
  const brandingFrom = parseBrandingCompanyDetails(branding?.contact_details);
  const hasBrandingFrom =
    Boolean(brandingFrom.name) || brandingFrom.lines.length > 0;
  const branchExtraLines = buildBranchFromExtraLines(branch, {
    includeBranchName: hasBrandingFrom,
  });

  const fromName = hasBrandingFrom
    ? brandingFrom.name || companyName
    : branchName || companyName || "—";
  const fromFields = hasBrandingFrom
    ? [
        ...(brandingFrom.fields || []),
        ...branchExtraLines.map((line) => {
          const match = String(line).match(/^([^:]+):\s*(.*)$/);
          if (match) {
            return { label: match[1].trim(), value: match[2].trim() };
          }
          return { label: "", value: line };
        }),
      ]
    : [
        {
          label: "Phone",
          value:
            branchPhone ||
            getInvoiceSettingPhoneLines(invoiceSetting).join(" / "),
        },
        {
          label: "Email",
          value: branchEmail || getInvoiceSettingEmail(invoiceSetting),
        },
        {
          label: "Website",
          value: formatInvoiceSettingWebsite(invoiceSetting?.website),
        },
        {
          label: "Address",
          value: [
            branchAddress,
            branchCountry,
            ...(!branchAddress
              ? getInvoiceSettingAddressLines(invoiceSetting)
              : []),
          ]
            .filter(Boolean)
            .join(", "),
        },
        ...buildBranchFromExtraLines(branch, { includeBranchName: false }).map(
          (line) => {
            const match = String(line).match(/^([^:]+):\s*(.*)$/);
            if (match) {
              return { label: match[1].trim(), value: match[2].trim() };
            }
            return { label: "", value: line };
          },
        ),
      ].filter((field) => String(field.value || "").trim());

  const fromOptions =
    draft?.previewDisplay?.invoiceDisplayOptions?.from || null;
  const processedFromFields = prepareFromPartyFields(
    fromFields,
    fromOptions || {},
  );

  return { companyName, fromName, fromFields: processedFromFields };
}

function renderPartyFields(fields = [], { forPdf = false } = {}) {
  const wrap = forPdf
    ? "word-break:break-word;overflow-wrap:anywhere;white-space:normal;"
    : "";
  const lineStyle = ` style="font-size:13px;color:#374151;margin:0 0 2px;line-height:1.45;${wrap}"`;
  const labelStyle = ` style="font-weight:700;color:#111827;${wrap}"`;
  const valueStyle = forPdf ? ` style="${wrap}"` : "";
  return (fields || [])
    .filter((field) => String(field?.value || "").trim())
    .map((field) => {
      if (!field.label) {
        return `<div class="party-line"${lineStyle}>${escapeHtml(field.value)}</div>`;
      }
      return `<div class="party-field"${lineStyle}><span class="party-field-label"${labelStyle}>${escapeHtml(
        field.label,
      )}: </span><span class="party-field-value"${valueStyle}>${escapeHtml(
        field.value,
      )}</span></div>`;
    })
    .join("");
}

function renderClassicMetaRow(label, valueHtml, { valueWrap = false } = {}) {
  const valueStyle = valueWrap
    ? "white-space:normal;word-break:break-word;overflow-wrap:anywhere;text-align:left;"
    : "white-space:nowrap;text-align:right;";
  return `<div class="classic-meta-row"><span class="classic-meta-label">${escapeHtml(
    label,
  )}</span><span class="classic-meta-value" style="${valueStyle}">${
    valueHtml || "—"
  }</span></div>`;
}

function renderMetaRow(label, valueHtml) {
  return `
    <div style="margin:0;text-align:right;font-size:13px;line-height:1.35;">
      <span style="color:#4b5563;font-weight:600;padding-right:8px;">${escapeHtml(label)}</span>
      <span style="color:#111827;font-weight:700;">${valueHtml}</span>
    </div>
  `;
}

function renderPaymentAdvice(content, footerOptions = null) {
  const blocks = filterPaymentAdviceBlocks(
    parsePaymentAdviceContent(content),
    footerOptions,
  );
  if (!blocks.length) return "";

  return blocks
    .map((block) => {
      if (block.type === "field") {
        const label = String(block.label || "").endsWith(":")
          ? block.label
          : `${block.label}:`;
        const value = block.value || (block.valueLines || []).join(" ");
        const isLongLabel = label.length > 32;

        if (isLongLabel) {
          return `
            <div class="footer-field footer-field--stacked" style="margin:0 0 4px;font-size:13px;line-height:1.5;">
              <div class="footer-field-label" style="font-weight:700;color:#111827;">${escapeHtml(label)}</div>
              <div class="footer-field-value" style="color:#374151;">${escapeHtml(value || "—")}</div>
            </div>
          `;
        }

        return `
          <div class="footer-field" style="margin:0 0 4px;font-size:13px;line-height:1.5;">
            <span class="footer-field-label" style="font-weight:700;color:#111827;">${escapeHtml(label)} </span>
            <span class="footer-field-value" style="color:#374151;">${escapeHtml(value || "—")}</span>
          </div>
        `;
      }
      return `<div class="footer-line" style="margin:0 0 4px;font-size:13px;line-height:1.5;color:#374151;">${escapeHtml(block.text || "")}</div>`;
    })
    .join("");
}

function renderTerms(content) {
  const lines = parseTermsConditionsContent(content);
  if (!lines.length) return "";
  return lines
    .map((line) => {
      const text = String(line || "").replace(/^\d+\.\s*/, "").trim();
      const display = text.startsWith("*") ? text : `* ${text}`;
      return `<div class="footer-line" style="margin:0 0 4px;font-size:13px;line-height:1.5;color:#374151;">${escapeHtml(display)}</div>`;
    })
    .join("");
}

/**
 * Builds self-contained HTML matching the InvoiceXeroPreview modal sheet UI
 * for the sendinvoice API email payload.
 */
export function buildInvoiceEmailHtml(draft, options = {}) {
  if (!draft) return "";
  const forPdf = Boolean(options.forPdf);
  const lineHeaderCellStyle = forPdf
    ? ' style="background-color:#ebf7ed;font-size:13px;font-weight:700;color:#111827;"'
    : "";
  const headerBandInlineStyle = forPdf
    ? ' style="background-color:#ebf7ed;padding:14px 8px 12px;"'
    : "";
  const lineBodyCellStyle = forPdf
    ? ' style="text-align:right;vertical-align:top;font-size:13px;color:#111827;"'
    : "";
  const lineDescCellStyle = forPdf
    ? ' style="vertical-align:top;font-size:13px;color:#111827;line-height:1.5;"'
    : ' style="vertical-align:top;"';
  const lineEndCell = forPdf
    ? lineBodyCellStyle
    : ' class="text-end"';
  const lineHeaderEndCell = forPdf
    ? ' style="text-align:right;background-color:#ebf7ed;font-size:13px;font-weight:700;color:#111827;"'
    : ` class="text-end"${lineHeaderCellStyle}`;

  const {
    documentType,
    brandingTitle,
    invoiceNumber,
    issueDate,
    dueDate,
    reference,
    clientProfile,
    currency = "AUD",
    branding,
    invoiceSetting,
    lineItems = [],
    gross_subtotal: grossSubtotal = 0,
    discount_total: discountTotal = 0,
    subtotal = 0,
    tax_amount: taxAmount = 0,
    inclusive_tax_amount: inclusiveTaxAmount = 0,
    exclusive_tax_amount: exclusiveTaxAmount = 0,
    total_amount: totalAmount = 0,
    paid_amount: paidAmount = 0,
    amount_due: amountDueValue,
    paymentDate = "",
  } = draft;

  const paidTotal = Number(paidAmount) || 0;
  const previewTotals = resolveInvoicePreviewTotals({
    gross_subtotal: grossSubtotal,
    subtotal,
    discount_total: discountTotal,
    total_amount: totalAmount,
    paid_amount: paidTotal,
    amount_due: amountDueValue,
    lineItems,
  });
  const {
    displaySubtotal: baseDisplaySubtotal,
    displayDiscount,
    hasDiscount,
    displayTotal: baseDisplayTotal,
    displayAmountDue: baseDisplayAmountDue,
    paidTotal: previewPaidTotal,
  } = previewTotals;

  const previewDisplay = draft?.previewDisplay || {};
  const showLineDiscount = previewDisplay.showLineDiscount !== false;
  const showDiscountTotal = previewDisplay.showDiscountTotal !== false;
  const useCommissionLineLayout = Boolean(previewDisplay.useCommissionLineLayout);
  const useExTaxLineLayout = Boolean(
    previewDisplay.useExTaxLineLayout || previewDisplay.useCommissionLineLayout,
  );

  let displaySubtotal = baseDisplaySubtotal;
  let displayTotal = baseDisplayTotal;
  let displayAmountDue = baseDisplayAmountDue;
  let footerTaxLabel = "";
  let displayTaxAmount = 0;

  if (useExTaxLineLayout) {
    const computedItems = (lineItems || []).map((row) =>
      buildComputedLineItem(row, row.tax_type),
    );
    const footerTotals = resolveExTaxFooterTotals(
      computedItems,
      draft?.taxType,
    );
    displaySubtotal = footerTotals.subtotalExTax;
    displayTotal = footerTotals.total;
    footerTaxLabel = footerTotals.taxLabel;
    displayTaxAmount = footerTotals.gst;
    displayAmountDue = resolveInvoiceDocumentAmountDue({
      displayTotal,
      paidTotal: previewPaidTotal,
      explicitAmountDue: amountDueValue,
    });
  } else {
    const hasInclusiveTax = inclusiveTaxAmount > 0;
    const hasExclusiveTax = exclusiveTaxAmount > 0;
    footerTaxLabel = resolvePreviewTaxLabel({
      lineItems,
      inclusiveTaxAmount,
      exclusiveTaxAmount,
      taxAmount,
      documentTaxType: draft?.taxType,
    });
    displayTaxAmount = hasExclusiveTax
      ? exclusiveTaxAmount
      : hasInclusiveTax
        ? inclusiveTaxAmount
        : Number(taxAmount) || 0;
  }

  const documentTitle =
    brandingTitle ||
    resolveInvoiceDisplayTitle({ branding, documentType });
  const { companyName, fromName, fromFields } = buildFromBlock(draft);
  const displayOpts = normalizeInvoiceDisplayOptions(
    previewDisplay?.invoiceDisplayOptions,
  );
  const showLogo = displayOpts.header.showLogo !== false;
  const logoUrl = showLogo
    ? options.logoDataUrl ||
      options.logoUrl ||
      resolveInvoiceEmailBrandLogoUrl({ invoiceSetting })
    : "";
  const abn = String(invoiceSetting?.abn || "").trim();
  const visibleLines = (lineItems || []).filter(
    (row) => row.description?.trim() || row.unit_price > 0,
  );
  const taxLabel = footerTaxLabel;

  const lineRows = visibleLines.length
    ? visibleLines
        .map((row) => {
          const description = String(row.description || "—");
          const descriptionHtml = escapeHtml(description).replace(
            /\r?\n/g,
            "<br/>",
          );
          return `
            <tr>
              <td${lineDescCellStyle}>${descriptionHtml}</td>
              <td${lineEndCell}>${
                row.quantity != null && row.quantity !== ""
                  ? escapeHtml(
                      Number(row.quantity) === parseInt(row.quantity, 10)
                        ? String(parseInt(row.quantity, 10))
                        : String(row.quantity),
                    )
                  : "—"
              }</td>
              <td${lineEndCell}>${formatAmount(row.unit_price)}</td>
              ${
                showLineDiscount
                  ? `<td${lineEndCell}>${formatAmount(row.discount)}</td>`
                  : ""
              }
              <td${lineEndCell}>${escapeHtml(
                useExTaxLineLayout
                  ? formatCommissionPreviewGstRate(row)
                  : formatLineItemTaxRateDisplay(row.tax_rate, row.tax_type),
              )}</td>
              <td${lineEndCell}>${formatAmount(
                useExTaxLineLayout
                  ? getLineDisplayTotalAmount(row)
                  : (row.amount ?? 0),
              )}</td>
            </tr>
          `;
        })
        .join("")
    : `<tr><td colspan="${
        showLineDiscount ? 6 : 5
      }" style="text-align:center;color:#6b7280;padding:24px;">No line items added yet.</td></tr>`;

  const totalsRows = [
    renderTotalRow("Subtotal", formatAmount(displaySubtotal), { forPdf }),
    showDiscountTotal && hasDiscount
      ? renderTotalRow("Discount", `-${formatAmount(displayDiscount)}`, {
          variant: "discount",
          forPdf,
        })
      : "",
    taxLabel && displayTaxAmount > 0
      ? renderTotalRow(taxLabel, formatAmount(displayTaxAmount), {
          variant: "tax",
          forPdf,
        })
      : "",
    renderTotalRow(`TOTAL ${currency}`, formatAmount(displayTotal), {
      variant: "grand",
      showDividerBefore: true,
      forPdf,
    }),
    previewPaidTotal > 0
      ? renderLessPaidTotalRow(paymentDate, formatAmount(previewPaidTotal))
      : "",
    renderTotalRow(`AMOUNT DUE ${currency}`, formatAmount(displayAmountDue), {
      variant: "due",
      showDividerBefore: previewPaidTotal > 0,
      forPdf,
    }),
  ].join("");

  const hasPaymentAdvice =
    Boolean(branding?.payment_advice) &&
    displayOpts.footer.showPaymentAdvice !== false;
  const hasTerms =
    Boolean(branding?.terms_conditions) &&
    displayOpts.footer.showTerms !== false;
  const footerPdfColumnWidths =
    hasPaymentAdvice && hasTerms ? "['*','*']" : "['*']";
  const footer =
    hasPaymentAdvice || hasTerms
      ? forPdf
        ? `
          <table class="footer-grid" role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0;padding:4px 0 0;border-top:1px solid #e5e7eb;" data-pdfmake="{'layout':'noBorders','widths':${footerPdfColumnWidths}}">
            <tr>
              ${
                hasPaymentAdvice
                  ? `<td style="vertical-align:top;width:50%;padding:0 24px 0 0;border:none;">
                      <div class="footer-heading" style="font-size:14px;font-weight:700;margin:0 0 6px;color:#111827;">Payment Advice</div>
                      ${renderPaymentAdvice(
                        branding.payment_advice,
                        displayOpts.footer,
                      )}
                    </td>`
                  : ""
              }
              ${
                hasTerms
                  ? `<td style="vertical-align:top;width:50%;padding:0;border:none;">
                      <div class="footer-heading" style="font-size:14px;font-weight:700;margin:0 0 6px;color:#111827;">Payment Terms and Conditions:</div>
                      ${renderTerms(branding.terms_conditions)}
                    </td>`
                  : ""
              }
            </tr>
          </table>
        `
        : `
        <div class="footer" style="margin:0;padding:4px 0 0;border-top:1px solid #e5e7eb;">
          <table class="footer-grid" role="presentation" width="100%" cellpadding="0" cellspacing="0" data-pdfmake="{'layout':'noBorders','widths':['*','*']}">
            <tr>
              ${
                hasPaymentAdvice
                  ? `<td style="vertical-align:top;width:50%;padding:0 24px 0 0;border:none;">
                      <div class="footer-heading" style="font-size:14px;font-weight:700;margin:0 0 6px;color:#111827;">Payment Advice</div>
                      ${renderPaymentAdvice(
                        branding.payment_advice,
                        displayOpts.footer,
                      )}
                    </td>`
                  : "<td></td>"
              }
              ${
                hasTerms
                  ? `<td style="vertical-align:top;width:50%;padding:0;border:none;">
                      <div class="footer-heading" style="font-size:14px;font-weight:700;margin:0 0 6px;color:#111827;">Payment Terms and Conditions:</div>
                      ${renderTerms(branding.terms_conditions)}
                    </td>`
                  : hasPaymentAdvice
                    ? "<td></td>"
                    : ""
              }
            </tr>
          </table>
        </div>
      `
      : "";

  const brandingFrom = parseBrandingCompanyDetails(branding?.contact_details);
  const hasBrandingFrom =
    Boolean(brandingFrom.name) || brandingFrom.lines.length > 0;

  const billToLines = useCommissionLineLayout
    ? previewDisplay?.commissionBillToPrintOptions
      ? buildCommissionInvoiceBillToLines(
          clientProfile,
          previewDisplay.commissionBillToPrintOptions,
        )
      : buildClassicInvoiceBillToLines(clientProfile)
    : [];

  const companyContact = useCommissionLineLayout
    ? buildClassicInvoiceCompanyContact({
        invoiceSetting,
        branch: draft.branch,
        brandingFrom: hasBrandingFrom ? brandingFrom : null,
        companyName,
      })
    : { name: "", fieldRows: [] };

  const classicHeader = useCommissionLineLayout
    ? `
    <div class="classic-top">
      <h1 class="classic-title">${escapeHtml(documentTitle)}</h1>
      <img class="classic-logo" src="${escapeHtml(logoUrl)}" alt="${escapeHtml(
        companyName,
      )}" />
    </div>
    <table class="classic-info" role="presentation" width="100%" cellpadding="0" cellspacing="0" data-pdfmake="{'layout':'noBorders'}">
      <tr>
        <td width="45%">
          <div class="classic-billto-label">To</div>
          ${
            billToLines.length
              ? billToLines
                  .map(
                    (line) =>
                      `<div class="classic-billto-line">${escapeHtml(line)}</div>`,
                  )
                  .join("")
              : '<div class="classic-billto-line">—</div>'
          }
        </td>
        <td width="28%">
          ${renderClassicMetaRow("Invoice Date", formatPreviewDate(issueDate))}
          ${renderClassicMetaRow(
            "Invoice Number",
            escapeHtml(invoiceNumber || "—"),
          )}
          ${renderClassicMetaRow("Reference", escapeHtml(reference || "—"), {
            valueWrap: true,
          })}
          ${abn ? renderClassicMetaRow("ABN", escapeHtml(abn)) : ""}
        </td>
        <td width="27%" class="classic-company">
          ${
            companyContact.name
              ? `<div class="classic-company-name">${escapeHtml(
                  companyContact.name,
                )}</div>`
              : ""
          }
          ${companyContact.fieldRows
            .map((row) =>
              renderClassicMetaRow(
                row.label,
                escapeHtml(row.value),
                { valueWrap: true },
              ),
            )
            .join("")}
        </td>
      </tr>
    </table>
  `
    : "";

  const logoCellContent = logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(
        companyName,
      )}" width="178" height="28" style="width:178px;height:28px;object-fit:contain;display:block;margin:0;padding:0;border:0;line-height:1;" />`
    : `<span style="display:block;width:178px;height:28px;line-height:28px;font-size:1px;">&nbsp;</span>`;

  const standardHeaderBand = forPdf
    ? `
    <table width="100%" cellpadding="0" cellspacing="0" data-pdfmake="{'layout':'invoiceHeader','widths':['*','*']}" style="width:100%;border-collapse:collapse;background-color:#ebf7ed;">
      <tr>
        <td width="50%" style="vertical-align:middle;background-color:#ebf7ed;padding:0;margin:0;line-height:1;">
          ${logoCellContent}
        </td>
        <td width="50%" style="vertical-align:middle;text-align:right;background-color:#ebf7ed;padding:0;margin:0;line-height:1;">
          <div style="margin:0;padding:0;font-size:18px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:#0c2268;line-height:1;text-align:right;">${escapeHtml(
            documentTitle,
          )}</div>
          ${
            invoiceNumber
              ? `<div style="margin:0;padding:0;font-size:11px;line-height:1.15;text-align:right;color:#64748b;"><span style="font-weight:500;">Invoice No.</span><span style="margin-left:4px;font-weight:600;color:#334155;">${escapeHtml(
                  invoiceNumber,
                )}</span></div>`
              : ""
          }
        </td>
      </tr>
    </table>
  `
    : `
    <div class="header-band"${headerBandInlineStyle}>
      <div class="header-brand" style="min-width:178px;min-height:28px;">
        ${
          logoUrl
            ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(companyName)}" />`
            : ""
        }
      </div>
      <div class="header-title-wrap" style="text-align:right;">
        <h1 class="doc-title">${escapeHtml(documentTitle)}</h1>
        ${
          invoiceNumber
            ? `<div class="header-meta"><span>Invoice No.</span><span class="header-meta-value">${escapeHtml(
                invoiceNumber,
              )}</span></div>`
            : ""
        }
      </div>
    </div>
  `;

  const commissionBillToPrintOptionsForParties =
    previewDisplay?.invoiceDisplayOptions
      ? toCommissionPartyPrintOptions(previewDisplay.invoiceDisplayOptions)
      : previewDisplay?.commissionBillToPrintOptions || null;
  const toShowProviderName =
    displayOpts.to.showName !== false &&
    Boolean(commissionBillToPrintOptionsForParties);
  const toProviderName = String(clientProfile?.fullName || "").trim();
  const toPartyFields =
    commissionBillToPrintOptionsForParties &&
    displayOpts.to.showSection !== false
      ? buildCommissionInvoicePartyFields(
          clientProfile,
          commissionBillToPrintOptionsForParties,
        ).filter((field) => field.label !== "Name")
      : displayOpts.to.showSection === false
        ? []
        : buildInvoiceClientPartyFields(clientProfile);

  const partyCellStyle = forPdf
    ? ' style="vertical-align:top;width:33.33%;padding:0 18px 0 0;word-break:break-word;overflow-wrap:anywhere;"'
    : "";
  const partyLabelStyle = forPdf
    ? ' style="font-size:11px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;color:#4b5563;margin:0 0 4px;"'
    : "";
  const partyNameStyle = forPdf
    ? ' style="font-size:13px;font-weight:700;color:#111827;margin:0 0 4px;line-height:1.4;word-break:break-word;overflow-wrap:anywhere;white-space:normal;"'
    : "";

  const showFromSection = displayOpts.from.showSection !== false;
  const showFromName =
    showFromSection && displayOpts.from.showName !== false;
  const showDetailsSection = displayOpts.details.showSection !== false;

  const standardPartiesBlock = `
    <table class="parties" role="presentation" width="100%" cellpadding="0" cellspacing="0"${
      forPdf
        ? ` data-pdfmake="{'layout':'noBorders','widths':['*','*','*']}" style="width:100%;border-collapse:collapse;margin:0;"`
        : ""
    }>
      <tr>
        ${
          displayOpts.to.showSection !== false
            ? `<td${partyCellStyle}>
          <div class="party-label"${partyLabelStyle}>To</div>
          ${
            toShowProviderName && toProviderName
              ? `<div class="party-name"${partyNameStyle}>${escapeHtml(
                  toProviderName,
                )}</div>`
              : ""
          }
          ${renderPartyFields(toPartyFields, { forPdf })}
        </td>`
            : ""
        }
        ${
          showFromSection
            ? `<td${partyCellStyle}>
          <div class="party-label"${partyLabelStyle}>From</div>
          ${
            showFromName
              ? `<div class="party-name"${partyNameStyle}>${escapeHtml(
                  fromName,
                )}</div>`
              : ""
          }
          ${renderPartyFields(fromFields, { forPdf })}
        </td>`
            : ""
        }
        ${
          showDetailsSection
            ? `<td${
                forPdf
                  ? ' style="vertical-align:top;width:33.33%;padding:4px 0 4px 12px;text-align:right;"'
                  : ""
              }>
          <div class="party-label"${partyLabelStyle}>Invoice details</div>
          ${
            displayOpts.details.showIssueDate !== false
              ? renderMetaRow("Issue date", formatPreviewDate(issueDate))
              : ""
          }
          ${
            displayOpts.details.showInvoiceNumber !== false
              ? renderMetaRow(
                  "Invoice number",
                  escapeHtml(invoiceNumber || "—"),
                )
              : ""
          }
          ${
            displayOpts.details.showReference !== false
              ? renderMetaRow("Reference", escapeHtml(reference || "—"))
              : ""
          }
          ${
            displayOpts.details.showDueDate !== false
              ? renderMetaRow("Due date", formatPreviewDate(dueDate))
              : ""
          }
          ${
            previewPaidTotal > 0 &&
            paymentDate &&
            displayOpts.details.showPaymentDate !== false
              ? renderMetaRow(
                  "Payment date",
                  formatInvoicePreviewMetaDate(paymentDate),
                )
              : ""
          }
          ${
            abn && displayOpts.details.showAbn !== false
              ? renderMetaRow("ABN", escapeHtml(abn))
              : ""
          }
        </td>`
            : ""
        }
      </tr>
    </table>
  `;

  const invoiceMainBodyContent = `
    ${useCommissionLineLayout ? "" : standardPartiesBlock}

    <table class="lines" width="100%" cellpadding="0" cellspacing="0"${
      forPdf
        ? ` style="margin:0;" data-pdfmake="{'layout':'invoiceLines','widths':${
            showLineDiscount
              ? "['*',36,54,46,40,64]"
              : "['*',36,54,40,64]"
          }}"`
        : ""
    }>
      <thead>
        <tr>
          <th${lineHeaderCellStyle}>Description</th>
          <th${lineHeaderEndCell}>${
            useCommissionLineLayout ? "Quantity" : "Qty."
          }</th>
          <th${lineHeaderEndCell}>${
            useCommissionLineLayout ? "Unit Price" : "Unit price"
          }</th>
          ${
            showLineDiscount
              ? `<th${lineHeaderEndCell}>Discount</th>`
              : ""
          }
          <th${lineHeaderEndCell}>${
            useCommissionLineLayout ? "GST" : "Tax rate"
          }</th>
          <th${lineHeaderEndCell}>Amount ${escapeHtml(currency)}</th>
        </tr>
      </thead>
      <tbody>${lineRows}</tbody>
    </table>

    ${
      forPdf
        ? `<table class="totals-align" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 12px 0;border-collapse:collapse;" data-pdfmake="{'layout':'noBorders','widths':['*',272]}"><tr><td style="margin:0;padding:0;"></td><td style="margin:0;padding:0;vertical-align:top;text-align:right;">
      <table class="totals" role="presentation" width="272" cellpadding="0" cellspacing="0" style="width:272px;border-collapse:collapse;" data-pdfmake="{'layout':'invoiceTotals','widths':['*','auto']}">${totalsRows}</table>
      </td></tr></table>`
        : `<div class="totals-wrap">
      <table class="totals" role="presentation" cellpadding="0" cellspacing="0">${totalsRows}</table>
    </div>`
    }

    ${footer}
  `;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(documentTitle)} ${escapeHtml(invoiceNumber || "")}</title>
  <style type="text/css">${EMAIL_INVOICE_CSS}${
    forPdf ? EMAIL_INVOICE_PDF_OVERRIDES_CSS : ""
  }</style>
</head>
<body${
    forPdf ? ' class="invoice-pdf-body"' : ""
  }>
  ${
    forPdf
      ? `${useCommissionLineLayout ? classicHeader : standardHeaderBand}${invoiceMainBodyContent}`
      : `<div class="sheet${useCommissionLineLayout ? " sheet--classic" : ""}">
    ${useCommissionLineLayout ? classicHeader : standardHeaderBand}
    <div class="sheet-body${
      useCommissionLineLayout ? " sheet-body--classic" : ""
    }">${invoiceMainBodyContent}</div>
  </div>`
  }
</body>
</html>`;
}
